/**
 * Where a CRM link points (A7Y-4982).
 *
 * The record was imported from `marketing/growth/pipeline.md` in a7y-ai-dev,
 * whose links are relative to that file (`../sales/king/call-prep.md`) or are
 * `#acct-…` anchors to another account's section. The account page opens them
 * on GitHub, or as the CRM account page, so a link copied out of the markdown
 * still lands somewhere real. Anything else that is not http(s) is refused.
 */
import { helmLinkLabel, helmPlainText } from '@a7y/platform-health/helm';

export const AI_DEV_REPO_URL = 'https://github.com/ApptonomyInc/a7y-ai-dev';
/** The directory every relative link in the imported record is relative to. */
const PIPELINE_DIR = 'marketing/growth';
const LINEAR_ISSUE_URL = 'https://linear.app/a7y/issue';
const NOTES_FOLDER_RE = /^marketing\/(sales|partnerships)\/([^/]+)/;
const TICKET_RE = /\bA7Y-\d+\b/g;

export interface ResolvedLink {
  href: string;
  /** True for a link that opens a CRM page in this app rather than a new tab. */
  internal: boolean;
}

/** A link as the record stores it: `[label](target)`, or a bare target. */
export interface ParsedLink {
  label: string;
  target: string;
}

export function parseLink(raw: string): ParsedLink {
  const text = raw.trim();
  const md = /^\[([^\]]*)\]\(([^)\s]+)\)$/.exec(text);
  // A7Y-5873: "pipeline §" reads "account page", never the sign.
  if (md) return { label: md[1] ? helmLinkLabel(md[1], md[2]) : md[2], target: md[2] };
  return { label: text, target: text };
}

/** `a/b/../c/./d` → `a/c/d`; null when it climbs out of the repository. */
function normalisePath(path: string): string | null {
  const out: string[] = [];
  for (const part of path.split('/')) {
    if (part === '' || part === '.') continue;
    if (part === '..') {
      if (out.length === 0) return null;
      out.pop();
    } else out.push(part);
  }
  return out.join('/');
}

/** The repository path a relative link names, or null when it names none. */
export function aiDevPath(target: string): string | null {
  if (/^[a-z][a-z0-9+.-]*:/i.test(target) || target.startsWith('#') || target.startsWith('//'))
    return null;
  const [path] = target.split('#');
  if (path === '') return null;
  const absolute = path.startsWith('/') ? path.slice(1) : `${PIPELINE_DIR}/${path}`;
  const normalised = normalisePath(absolute);
  if (normalised === null || normalised === '') return null;
  return path.endsWith('/') ? `${normalised}/` : normalised;
}

export function aiDevUrl(repoPath: string): string {
  const kind = repoPath.endsWith('/') ? 'tree' : 'blob';
  return `${AI_DEV_REPO_URL}/${kind}/main/${repoPath.replace(/\/$/, '')}`;
}

export function accountPagePath(accountId: string): string {
  return `/admin/cmo/crm/${encodeURIComponent(accountId)}`;
}

/** The pipeline file the record was imported from; its `#acct-…` sections are now account pages. */
const PIPELINE_PATH = `${PIPELINE_DIR}/pipeline.md`;

/** Where a stored link opens, or null when it is not safe to open at all. */
export function resolveLink(target: string): ResolvedLink | null {
  // A7Y-5873: Markdown's `[note](<path>)` form names the same path as `[note](path)`.
  const t = target.trim().replace(/^<(.*)>$/, '$1');
  if (/^https?:\/\//i.test(t)) return { href: t, internal: false };
  const anchor = /^#(acct-[A-Za-z0-9_-]+)$/.exec(t);
  if (anchor) return { href: accountPagePath(anchor[1]), internal: true };
  const path = aiDevPath(t);
  if (path === null) return null;
  // A7Y-5342: `pipeline.md#acct-x` (a roster row's "pipeline §") is that account's page in the app now.
  const section = /#(acct-[A-Za-z0-9_-]+)$/.exec(t);
  if (path === PIPELINE_PATH && section)
    return { href: accountPagePath(section[1]), internal: true };
  return { href: aiDevUrl(path), internal: false };
}

export interface NotesFolder {
  /** e.g. `marketing/sales/king/` */
  path: string;
  href: string;
}

/** The account's notes folders in a7y-ai-dev, read off every link it carries, in first-seen order. */
export function notesFolders(targets: readonly string[]): NotesFolder[] {
  const seen = new Set<string>();
  const out: NotesFolder[] = [];
  for (const target of targets) {
    const path = aiDevPath(target);
    const m = path === null ? null : NOTES_FOLDER_RE.exec(path);
    if (!m) continue;
    const folder = `marketing/${m[1]}/${m[2]}/`;
    if (seen.has(folder)) continue;
    seen.add(folder);
    out.push({ path: folder, href: aiDevUrl(folder) });
  }
  return out;
}

/** Every markdown link target inside free text (notes, next action). */
export function linkTargetsIn(text: string | null): string[] {
  if (!text) return [];
  return [...text.matchAll(/\]\(([^)\s]+)\)/g)].map((m) => m[1]);
}

export function ticketUrl(ticket: string): string {
  return `${LINEAR_ISSUE_URL}/${ticket}`;
}

/** A7Y ticket ids named in a gate, in order, without repeats. */
export function ticketsIn(text: string): string[] {
  return [...new Set(text.match(TICKET_RE) ?? [])];
}

/**
 * An account name as the record holds it, split at the first link written into
 * it (A7Y-5492: "Replug ([rplg.io](https://www.rplg.io/))"): the plain name, and
 * the rest with its marks.
 */
export function splitAccountName(name: string): { name: string; rest: string } {
  const at = name.search(/\s*\(?\[[^\]]*\]\(/);
  if (at <= 0) return { name: helmPlainText(name), rest: '' };
  return { name: helmPlainText(name.slice(0, at)), rest: name.slice(at).trim() };
}
